/**
 * SOUND-643 — one status-history timeline for every CRM record.
 *
 * Three entities keep a statusHistory and NO TWO AGREE on its shape:
 *
 *   registrations    { from, to, reason, changedBy, changedAt }
 *   mids             { from, to, reason, changedBy, timestamp }
 *   midApplications  MIXED — rows written by the original createApplication are
 *                    { status, timestamp, userId } with no from/to at all;
 *                    rows written since carry both shapes at once.
 *
 * A per-page reader would read `changedAt` on a MID and get undefined, or read
 * `to` on an old MID-application row and render "undefined". Normalising once,
 * here, is the only way the three pages can look the same and be right.
 *
 * Pure and tested — the component that draws the rail cannot be tested in this
 * repo.
 */

/** Timestamp, whichever field the writer happened to use. */
export function entryTimestamp(entry) {
  return entry?.changedAt || entry?.timestamp || null;
}

/**
 * Destination status. `to` where present, else the flat `status` used by the
 * oldest MID-application rows.
 */
export function entryTo(entry) {
  return entry?.to || entry?.status || null;
}

/**
 * Who did it. `changedBy` is the modern shape; the oldest MID-application rows
 * carry a bare `userId`, which is at least honest about being an id.
 */
export function entryActor(entry) {
  return (
    entry?.changedBy?.name ||
    entry?.changedBy?.email ||
    entry?.userId ||
    null
  );
}

/**
 * Build rail entries from any of the three shapes.
 *
 * @param {object[]} history
 * @param {object} opts
 * @param {string|Date} [opts.createdAt]   prepends a creation entry
 * @param {string} [opts.createdLabel]
 * @param {(s:string)=>string} [opts.labelFor]  status -> human label
 * @param {(s:string)=>string} [opts.dotFor]    status -> tailwind bg class
 * @returns {Array<{key,dot,text,reason,actor,at}>} oldest first
 */
export function buildStatusTimeline(history = [], opts = {}) {
  const {
    createdAt = null,
    createdLabel = "Created",
    labelFor = (s) => s || "—",
    dotFor = () => "bg-brand",
  } = opts;

  const entries = [];

  if (createdAt) {
    entries.push({
      key: "created",
      dot: "bg-brand",
      text: createdLabel,
      reason: null,
      actor: null,
      at: createdAt,
    });
  }

  const rows = Array.isArray(history) ? history : [];

  rows.forEach((h, i) => {
    const to = entryTo(h);
    const from = h?.from || null;

    // The very first history row is usually the creation stamp itself
    // ({ status: "new" }). With a createdAt already prepended, rendering it
    // again gives two entries for one event — the exact duplication the
    // registration timeline was fixed for.
    const isCreationStamp =
      i === 0 && createdAt && !from && (to === "new" || to === null);
    if (isCreationStamp) return;

    entries.push({
      key: `h-${i}`,
      dot: dotFor(to),
      text: from ? `${labelFor(from)} → ${labelFor(to)}` : labelFor(to),
      reason: h?.reason || null,
      actor: entryActor(h),
      at: entryTimestamp(h),
    });
  });

  return entries.sort((a, b) => {
    // Undated entries keep their INSERTION position. Returning -1/1 would sort
    // them to one end and push a record's creation below rows that predate the
    // timestamp field.
    if (!a.at || !b.at) return 0;
    return new Date(a.at) - new Date(b.at);
  });
}
